import { useRef, useEffect } from 'react';

import { isFocusTrapped } from '../../Select.helpers';
import { useSelectRootContext } from '../root/SelectRootContext';
import { useSelectRootFloatingContext } from '../root/SelectRootFloatingContext';

/**
 * Custom hook that handles closing the Select dropdown when focus moves outside the component.
 *
 * This hook implements sophisticated focus management for Select components by:
 * - Monitoring mouse interactions to detect clicks outside the select area
 * - Handling edge cases where programmatic focus changes occur within dropdown actions
 * - Using timeouts to allow other UI interactions to complete before closing
 *
 * The hook addresses complex scenarios such as:
 * - Clicking on buttons inside the dropdown that trigger modals with programmatic focus
 * - Ensuring the floating focus manager properly handles focus transitions
 * - Preventing premature dropdown closure during legitimate user interactions

* @requires SelectRootContext - Must be used within a Select component tree
 * @sideEffects Adds/removes global mouseup event listeners when dropdown is open
 */
export const useCloseOnFocusOutside = () => {
    const { open, setOpen } = useSelectRootContext();
    const { floatingRefs } = useSelectRootFloatingContext();
    const closingFocusOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!open) return () => {};

        const closeIfFocusIsOutside = () => {
            const { activeElement } = document;

            if (activeElement === null || isFocusTrapped(floatingRefs, activeElement)) {
                // If the active element is inside the floating element or the select container, do nothing
                return;
            }
            setOpen(false);
        };

        const handleMouseUp = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (
                target === floatingRefs.domReference.current ||
                floatingRefs.domReference.current?.contains(target)
            ) {
                // If the click happened on the reference element, we don't need to close the dropdown
                return;
            }
            if (isFocusTrapped(floatingRefs, target)) {
                // Let's schedule a check to close the dropdown if focus moves outside
                // this can happen if the user clicks on a button inside the dropdown
                // that cause the focus to move outside the dropdown programmatically
                // e.g. a button that opens another modal which a `input.focus()` call.
                // unfortunately this is not handled by the `focusin` event which causes
                // the floating focus manager to not close the dropdown.
                closingFocusOutTimeoutRef.current = setTimeout(() => {
                    closeIfFocusIsOutside();
                }, 200);
            } else {
                // If click happened outside both the reference and floating elements we need to close the dropdown.
                // We need to delay it so other actions like clicking on other elements can be processed
                closingFocusOutTimeoutRef.current = setTimeout(() => {
                    setOpen(false);
                }, 200);
            }
        };
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleMouseUp);
            if (closingFocusOutTimeoutRef.current) {
                clearTimeout(closingFocusOutTimeoutRef.current);
                closingFocusOutTimeoutRef.current = null;
            }
        };
    }, [open, floatingRefs]);
};
